/**
 * MODULE: domain/sales-intelligence/frontend/components/ExportModal.tsx
 * PURPOSE: Modal for exporting research in PDF, JSON, or Markdown formats
 *
 * DEPENDENCIES: flowbite-react, useAccountActions
 * AGENT SAFETY: SAFE
 */

import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Tabs, TabItem } from 'flowbite-react';
import { HiDocumentText, HiShare } from 'react-icons/hi';
import { useAccountActions } from '../hooks/useAccountActions';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  insightId: string;
}

export function ExportModal({ isOpen, onClose, companyName, insightId }: ExportModalProps) {
  const [copiedLink, setCopiedLink] = useState(false);
  const { exportToPDF, exportToJSON, exportToMarkdown, shareProfile, loading, error } = useAccountActions();

  useEffect(() => {
    if (copiedLink) {
      const timer = setTimeout(() => setCopiedLink(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedLink]);

  const handleExport = async (format: 'pdf' | 'json' | 'markdown') => {
    try {
      if (format === 'pdf') await exportToPDF(insightId, companyName);
      else if (format === 'json') await exportToJSON(insightId, companyName);
      else await exportToMarkdown(insightId, companyName);
    } catch {
      // Error handled by useAccountActions
    }
  };

  const handleCopyLink = async () => {
    try {
      await shareProfile(insightId);
      setCopiedLink(true);
    } catch {
      // Error handled by useAccountActions
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="md">
      <ModalHeader>Export &amp; Share Research</ModalHeader>
      <ModalBody>
        <Tabs aria-label="Export or Share" variant="default">
          <TabItem active title="Export" icon={HiDocumentText}>
            <div className="py-4 space-y-3">
              <p className="text-sm text-gray-600">Choose a format to download the research report:</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleExport('pdf')}
                  disabled={loading}
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                >
                  <span className="text-2xl">📄</span>
                  <div>
                    <div className="font-medium text-gray-900">PDF Document</div>
                    <div className="text-sm text-gray-500">Professional report format</div>
                  </div>
                </button>
                <button
                  onClick={() => handleExport('json')}
                  disabled={loading}
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                >
                  <span className="text-2xl">💾</span>
                  <div>
                    <div className="font-medium text-gray-900">JSON Data</div>
                    <div className="text-sm text-gray-500">For integrations and CRM</div>
                  </div>
                </button>
                <button
                  onClick={() => handleExport('markdown')}
                  disabled={loading}
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                >
                  <span className="text-2xl">📝</span>
                  <div>
                    <div className="font-medium text-gray-900">Markdown</div>
                    <div className="text-sm text-gray-500">Easy to edit and customize</div>
                  </div>
                </button>
              </div>
            </div>
          </TabItem>
          <TabItem title="Share" icon={HiShare}>
            <div className="py-4 space-y-3">
              <p className="text-sm text-gray-600">Copy a shareable link to clipboard:</p>
              <button
                onClick={handleCopyLink}
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  copiedLink
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : copiedLink ? (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <HiShare className="w-5 h-5" />
                    Copy Link
                  </>
                )}
              </button>
              {copiedLink && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">Link copied! Share with your team to collaborate.</p>
                </div>
              )}
            </div>
          </TabItem>
        </Tabs>
        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}
